import React from 'react';
import { Shield, Lock, Eye, Database } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero */}
      <div className="bg-gradient-to-r from-gray-950 to-black py-20 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
            Privacy <span className="text-yellow-400">Policy</span>
          </h1>
          <p className="text-gray-400 text-lg">Last Updated: November 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Trust Indicators */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-3">
                <Shield className="text-yellow-400" size={32} />
              </div>
              <p className="text-gray-400 text-sm">Data Protected</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-3">
                <Lock className="text-yellow-400" size={32} />
              </div>
              <p className="text-gray-400 text-sm">Encrypted</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-3">
                <Eye className="text-yellow-400" size={32} />
              </div>
              <p className="text-gray-400 text-sm">No Third Party</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-3">
                <Database className="text-yellow-400" size={32} />
              </div>
              <p className="text-gray-400 text-sm">Minimal Data</p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">Introduction</h2>
              <p className="text-gray-300 leading-relaxed">
                At Indya Bungy, we are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, store, and protect your data when you use our services or visit our website.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-white font-bold mb-2">Personal Information:</h3>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start">
                      <span className="text-yellow-400 mr-2">•</span>
                      <span>Name, age, and contact details</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-400 mr-2">•</span>
                      <span>Email address and phone number</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-400 mr-2">•</span>
                      <span>Weight and health information (for safety purposes)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-400 mr-2">•</span>
                      <span>Government-issued ID for verification</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-bold mb-2">Booking Information:</h3>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start">
                      <span className="text-yellow-400 mr-2">•</span>
                      <span>Jump date, time, and location preferences</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-400 mr-2">•</span>
                      <span>Payment information (processed securely)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Your Data */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Your Data</h2>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>To process and confirm your bookings</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>To ensure safety compliance and verify eligibility</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>To communicate with you about your booking</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>To send promotional offers (with your consent)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>To improve our services and customer experience</span>
                </li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. Data Security</h2>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>All data is encrypted in transit and at rest</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>We use secure servers and follow industry best practices</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Payment information is processed through secure gateways</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Limited staff access to personal information</span>
                </li>
              </ul>
            </section>

            {/* Third-Party Sharing */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. Third-Party Sharing</h2>
              <p className="text-gray-300 mb-4">
                We do <strong className="text-white">NOT</strong> sell, trade, or rent your personal information to third parties. We may share data only in these limited circumstances:
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>With payment processors for secure transactions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>When required by law or legal process</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>To protect our rights and safety</span>
                </li>
              </ul>
            </section>

            {/* Your Rights */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. Your Rights</h2>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Access your personal data we hold</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Request correction of inaccurate data</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Request deletion of your data</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Opt-out of marketing communications</span>
                </li>
              </ul>
            </section>

            {/* Contact */}
            <section className="bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
              <p className="text-gray-300">
                For any privacy-related questions or concerns, please contact us at:
                <br />
                <strong className="text-white">Email:</strong> crew.indyabungy@gmail.com
                <br />
                <strong className="text-white">Phone:</strong> +91 8506865393
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;